'use client';

import { EcEnv } from '@castlery/config';
import { GlobalReview, Product, selectBundleVariants, selectVariant } from '@castlery/modules-product-domain';
import { getWebProductReviewsCommand } from '@castlery/modules-product-services';
import { useAppDispatch, useSelector } from '@castlery/shared-redux-store';
import { CountryCode } from '@castlery/utils';
import { useEffect, useMemo, useState, useRef } from 'react';
import { logger } from '@castlery/observability';

export enum OrderItems {
  recommended = 'recommended',
  mostRecent = 'most_recent',
  ratingHighToLow = 'rating_high_to_low',
  ratingLowToHigh = 'rating_low_to_high',
  withImage = 'with_image',
  productSelf = 'product_self',
}

export function useGlobalReviews(productData: Product | undefined) {
  const dispatch = useAppDispatch();
  const [orderItems, setOrderItems] = useState<OrderItems>(OrderItems.recommended);
  const [currentCountry, setCurrentCountry] = useState<CountryCode>(() => {
    const country = EcEnv.NEXT_PUBLIC_COUNTRY;
    if (country) {
      return country as CountryCode;
    }
    return CountryCode.ALL;
  });
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(3);
  const [isInitialPhase, setIsInitialPhase] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState<number>(0);
  const variant = useSelector(selectVariant);
  const bundleVariant = useSelector(selectBundleVariants);
  const [reviews, setReviews] = useState<GlobalReview[] | undefined>(undefined);
  const [averageRating, totalCount] = useMemo(() => {
    let averageRating = 0;
    let totalCount = 0;
    if (productData?.reviews && 'average_rating' in productData.reviews) {
      averageRating = productData.reviews.average_rating;
    }
    if (productData?.reviews && 'total_count' in productData.reviews) {
      totalCount = productData.reviews.total_count;
    }
    return [averageRating, totalCount];
  }, [productData]);

  const coreParams = useMemo(
    () => ({
      product: productData,
      variant,
      bundleVariant,
      country: currentCountry,
      orderBy: orderItems,
    }),
    [productData, variant, bundleVariant, currentCountry, orderItems]
  );

  const prevCoreParamsRef = useRef(coreParams);
  const isCoreParamsChanged = JSON.stringify(prevCoreParamsRef.current) !== JSON.stringify(coreParams);

  useEffect(() => {
    setPageNumber(1);
    setPerPage(3);
    prevCoreParamsRef.current = coreParams;
  }, [coreParams]);

  useEffect(() => {
    if (!coreParams.product || (!coreParams.variant && !coreParams.bundleVariant)) {
      return;
    }

    if (isCoreParamsChanged && pageNumber !== 1) {
      return;
    }

    const queryParams = {
      ...coreParams,
      pageNumber,
      perPage,
    };

    const getReviews = async () => {
      try {
        setReviewsLoading(true);
        const res = await dispatch(getWebProductReviewsCommand(queryParams)).unwrap();
        setReviews(res?.results);
        setTotalPages(res?.total_pages || 0);
      } catch (e) {
        logger.error('Failed to fetch product reviews', {
          error: e,
          productId: coreParams.product?.id,
          variantId: coreParams.variant?.id || coreParams.bundleVariant?.variant_id,
          country: coreParams.country,
          orderBy: coreParams.orderBy,
        });
        setReviews([]);
        setTotalPages(0);
      } finally {
        setReviewsLoading(false);
      }
    };
    getReviews();
  }, [coreParams, pageNumber, perPage, dispatch]);

  useEffect(() => {
    if (isInitialPhase && reviews !== undefined) {
      if (reviews?.length === 0 && currentCountry !== CountryCode.ALL) {
        setCurrentCountry(CountryCode.ALL);
      }
      setIsInitialPhase(false);
    }
  }, [isInitialPhase, reviews, currentCountry]);

  return {
    orderItems,
    setOrderItems,
    currentCountry,
    setCurrentCountry,
    pageNumber,
    setPageNumber,
    perPage,
    setPerPage,
    reviewsLoading,
    averageRating,
    totalCount,
    totalPages,
    reviews,
  };
}
